import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, DatePicker,
  Space, Popconfirm, message, Card, Switch, Badge, Tooltip, Select
} from 'antd';
import {
  PlusOutlined, CopyOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, SearchOutlined, UserOutlined, UserDeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const { Option } = Select;

const Serials = () => {
  const [data, setData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [assigningRecord, setAssigningRecord] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();
  const { getAuthHeaders, API_BASE_URL } = useAuth();

  useEffect(() => {
    fetchData();
    fetchCustomers();
  }, [pagination.current, pagination.pageSize, searchText]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/serials`, {
        headers: getAuthHeaders(),
        params: {
          page: pagination.current,
          per_page: pagination.pageSize,
          search: searchText
        }
      });
      if (response.data.success) {
        setData(response.data.data.items);
        setPagination(prev => ({ ...prev, total: response.data.data.total }));
      }
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/customers/all`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (error) {
      console.error('获取客户列表失败:', error);
    }
  };

  const handleCreate = async (values) => {
    try {
      const data = {
        max_activations: values.max_activations,
        notes: values.notes,
        expires_at: values.expires_at ? values.expires_at.toISOString() : null,
        customer_id: values.customer_id || null
      };
      
      const response = await axios.post(`${API_BASE_URL}/serials`, data, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        message.success('序列号创建成功');
        setModalVisible(false);
        form.resetFields();
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '创建失败');
    }
  };

  const handleUpdate = async (values) => {
    try {
      const data = {
        max_activations: values.max_activations,
        is_active: values.is_active,
        notes: values.notes,
        expires_at: values.expires_at ? values.expires_at.toISOString() : null
      };
      
      const response = await axios.put(`${API_BASE_URL}/serials/${editingRecord.id}`, data, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        message.success('序列号更新成功');
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/serials/${id}`, {
        headers: getAuthHeaders()
      });
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleAssign = async (values) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/serials/${assigningRecord.id}/assign`,
        { customer_id: values.customer_id },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        message.success('序列号分配成功');
        setAssignModalVisible(false);
        setAssigningRecord(null);
        assignForm.resetFields();
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '分配失败');
    }
  };

  const handleUnassign = async (record) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/serials/${record.id}/unassign`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        message.success('已解除客户分配');
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '解除分配失败');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('已复制到剪贴板');
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      expires_at: record.expires_at ? dayjs(record.expires_at) : null
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ max_activations: 1 });
    setModalVisible(true);
  };

  const openAssignModal = (record) => {
    setAssigningRecord(record);
    assignForm.setFieldsValue({ customer_id: record.customer_id });
    setAssignModalVisible(true);
  };

  const columns = [
    {
      title: '序列号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      render: (text) => (
        <Space>
          <code>{text}</code>
          <Tooltip title="复制">
            <Button icon={<CopyOutlined />} size="small" onClick={() => copyToClipboard(text)} />
          </Tooltip>
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'error'} 
          text={isActive ? '有效' : '禁用'} 
        />
      )
    },
    {
      title: '激活次数',
      key: 'activations',
      render: (_, record) => (
        <span>{record.current_activations} / {record.max_activations}</span>
      )
    },
    {
      title: '剩余次数',
      dataIndex: 'remaining_activations',
      key: 'remaining_activations',
      render: (num) => <span style={{ color: num > 0 ? '#52c41a' : '#ff4d4f' }}>{num}</span>
    },
    {
      title: '所属客户',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (text, record) => (
        text ? (
          <Space>
            <UserOutlined />
            <span>{text}</span>
            <Tooltip title="解除分配">
              <Button 
                icon={<UserDeleteOutlined />} 
                size="small" 
                danger 
                onClick={() => handleUnassign(record)}
              />
            </Tooltip>
          </Space>
        ) : (
          <Tooltip title="分配给客戶">
            <Button 
              size="small" 
              type="dashed" 
              icon={<UserOutlined />}
              onClick={() => openAssignModal(record)}
            >
              未分配
            </Button>
          </Tooltip>
        )
      )
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '永不过期'
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button icon={<EditOutlined />} size="small" onClick={() => openEditModal(record)} />
          </Tooltip>
          <Popconfirm
            title="确定要删除吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title="序列号管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            新建序列号
          </Button>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="搜索序列号或备注"
            allowClear
            onSearch={setSearchText}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
        </Space>
        
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={setPagination}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑序列号' : '新建序列号'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingRecord ? handleUpdate : handleCreate}
        >
          <Form.Item
            name="max_activations"
            label="最大激活次数"
            rules={[{ required: true, message: '请输入最大激活次数' }]}
          >
            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
          </Form.Item>
          
          {!editingRecord && (
            <Form.Item
              name="customer_id"
              label="分配给客戶"
            >
              <Select 
                placeholder="选择客户（可选）" 
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {customers.map(c => (
                  <Option key={c.id} value={c.id}>
                    {c.username} {c.company_name ? `(${c.company_name})` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          
          {editingRecord && (
            <Form.Item
              name="is_active"
              label="状态"
              valuePropName="checked"
            >
              <Switch checkedChildren="有效" unCheckedChildren="禁用" />
            </Form.Item>
          )}
          
          <Form.Item
            name="expires_at"
            label="过期时间"
          >
            <DatePicker style={{ width: '100%' }} placeholder="选择过期时间（可选）" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 分配客户模态框 */}
      <Modal
        title="分配序列号给客戶"
        open={assignModalVisible}
        onCancel={() => { setAssignModalVisible(false); setAssigningRecord(null); assignForm.resetFields(); }}
        onOk={() => assignForm.submit()}
        destroyOnClose
      >
        <Form
          form={assignForm}
          layout="vertical"
          onFinish={handleAssign}
        >
          {assigningRecord && (
            <p style={{ marginBottom: 16 }}>
              序列号: <code>{assigningRecord.serial_number}</code>
            </p>
          )}
          <Form.Item
            name="customer_id"
            label="选择客户"
            rules={[{ required: true, message: '请选择要分配的客户' }]}
          >
            <Select 
              placeholder="选择客户" 
              showSearch
              optionFilterProp="children"
            >
              {customers.map(c => (
                <Option key={c.id} value={c.id}>
                  {c.username} {c.company_name ? `(${c.company_name})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Serials;
